import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from './firebase';
import { clearUser } from './redux/authSlice';
import { Search, Upload, LogOut, ChevronRight, X, Shield, ChevronDown } from 'lucide-react';
import { getCategories, getSoftwareByCategory, searchSoftware } from './services/db';
import { trackCategoryBrowsed, trackSearchPerformed } from './services/analytics';
import QuoteUpload from './QuoteUpload';
import SoftwareDetail from './SoftwareDetail';
import { SkeletonCard } from './components/SkeletonCard';
import './BetaDashboard.css';

const PRICING_MODEL_OPTIONS = [
  { value: '', label: 'All pricing models' },
  { value: 'per-seat', label: 'Per Seat / Per User' },
  { value: 'flat', label: 'Flat Rate' },
  { value: 'usage', label: 'Usage Based' },
  { value: 'custom', label: 'Custom / Contact Sales' },
];

const SORT_OPTIONS = [
  { value: 'name-asc', label: 'Name (A → Z)' },
  { value: 'name-desc', label: 'Name (Z → A)' },
  { value: 'quotes-desc', label: 'Most Community Quotes' },
  { value: 'price-asc', label: 'Lowest Avg Price' },
];

const BetaDashboard = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [software, setSoftware] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [softwareLoading, setSoftwareLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedSoftware, setSelectedSoftware] = useState(null);
  // DEV-10: Sort/filter state
  const [filterModel, setFilterModel] = useState('');
  const [sortBy, setSortBy] = useState('name-asc');
  const { user, isAdmin } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const debounceRef = useRef(null);

  // Load categories on mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const cats = await getCategories();
        setCategories(cats);
      } catch (error) {
        console.error('Error loading categories:', error);
      } finally {
        setLoading(false);
      }
    };
    loadCategories();
  }, []);

  // Load software when category changes — reset pagination + filters
  useEffect(() => {
    if (!selectedCategory) {
      setSoftware([]);
      setLastDoc(null);
      setHasMore(false);
      return;
    }
    setFilterModel('');
    setSortBy('name-asc');
    const loadSoftware = async () => {
      setSoftwareLoading(true);
      try {
        const result = await getSoftwareByCategory(selectedCategory, null);
        setSoftware(result.items);
        setLastDoc(result.lastVisible);
        setHasMore(result.hasMore);
        setSearchResults(null);
        const catObj = categories.find(c => c.id === selectedCategory);
        if (catObj) trackCategoryBrowsed(catObj.name);
      } catch (error) {
        console.error('Error loading software:', error);
      } finally {
        setSoftwareLoading(false);
      }
    };
    loadSoftware();
  }, [selectedCategory]);

  // DEV-9: Debounced live search (300ms)
  const runSearch = useCallback(async (query) => {
    if (!query.trim()) {
      setSearchResults(null);
      return;
    }
    setSoftwareLoading(true);
    trackSearchPerformed(query.trim().length);
    try {
      const results = await searchSoftware(query);
      setSearchResults(results);
      setSelectedCategory(null);
      setSoftware([]);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSoftwareLoading(false);
    }
  }, []);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(val), 300);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    clearTimeout(debounceRef.current);
    runSearch(searchQuery);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
    clearTimeout(debounceRef.current);
  };

  // DEV-11: Load more
  const handleLoadMore = async () => {
    if (!selectedCategory || !lastDoc) return;
    setLoadingMore(true);
    try {
      const result = await getSoftwareByCategory(selectedCategory, lastDoc);
      setSoftware(prev => [...prev, ...result.items]);
      setLastDoc(result.lastVisible);
      setHasMore(result.hasMore);
    } catch (error) {
      console.error('Error loading more:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      dispatch(clearUser());
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleLogoClick = () => {
    setSearchQuery('');
    setSearchResults(null);
    setSelectedCategory(null);
    setSoftware([]);
    setSelectedSoftware(null);
    clearTimeout(debounceRef.current);
  };

  // DEV-10: Apply filter + sort to the loaded software list
  const getDisplaySoftware = () => {
    let list = [...software];

    if (filterModel) {
      const isPer = filterModel === 'per-seat';
      const isCustom = filterModel === 'custom';
      list = list.filter(sw => {
        if (isCustom) return sw.pricingModel === 'custom' || sw.enterpriseContact;
        if (isPer) return sw.pricingModel === 'per-seat' || sw.pricingModel === 'per-user';
        return sw.pricingModel === filterModel;
      });
    }

    list.sort((a, b) => {
      if (sortBy === 'name-asc') return a.name.localeCompare(b.name);
      if (sortBy === 'name-desc') return b.name.localeCompare(a.name);
      if (sortBy === 'quotes-desc') {
        return (b.pricingSummary?.quoteCount || 0) - (a.pricingSummary?.quoteCount || 0);
      }
      if (sortBy === 'price-asc') {
        const pa = a.pricingSummary?.avgPerSeat ?? Infinity;
        const pb = b.pricingSummary?.avgPerSeat ?? Infinity;
        return pa - pb;
      }
      return 0;
    });

    return list;
  };

  const getPricingPreview = (sw) => {
    if (sw.pricingSummary?.avgPerSeat) {
      return {
        type: 'community',
        price: sw.pricingSummary.avgPerSeat,
        label: '/seat avg',
        subtext: sw.pricingSummary.quoteCount ? `${sw.pricingSummary.quoteCount} quotes` : null
      };
    }
    if (sw.tiers?.length > 0) {
      const tiersWithPrice = sw.tiers.filter(t => t.price !== null && t.price !== undefined);
      if (tiersWithPrice.length > 0) {
        const lowestTier = tiersWithPrice.reduce((min, t) => (t.price < min.price) ? t : min, tiersWithPrice[0]);
        return {
          type: 'tier',
          price: lowestTier.price,
          label: lowestTier.unit ? `/${lowestTier.unit}` : '',
          subtext: `Starting at • ${tiersWithPrice.length} tier${tiersWithPrice.length > 1 ? 's' : ''}`
        };
      }
      return { type: 'contact', label: 'Contact Sales', subtext: `${sw.tiers.length} tier${sw.tiers.length > 1 ? 's' : ''} available` };
    }
    if (sw.pricingModel === 'custom' || sw.enterpriseContact) {
      return { type: 'contact', label: 'Contact Sales', subtext: sw.pricingModel ? `${sw.pricingModel} pricing` : null };
    }
    return { type: 'none', label: 'No pricing data' };
  };

  const formatPricingModel = (model) => {
    const models = {
      'per-user': 'Per User', 'per-seat': 'Per Seat', 'per-endpoint': 'Per Endpoint',
      'per-device': 'Per Device', 'per-technician': 'Per Technician', 'per-employee': 'Per Employee',
      'per-member': 'Per Member', 'per-computer': 'Per Computer', 'per-workstation': 'Per Workstation',
      'flat': 'Flat Rate', 'usage': 'Usage Based', 'custom': 'Custom', 'hybrid': 'Hybrid'
    };
    return models[model] || model;
  };

  const getCategoryName = (catId) => {
    const cat = categories.find(c => c.id === catId);
    return cat ? cat.name : catId;
  };

  const handleSoftwareClick = (sw) => {
    setSelectedSoftware({ ...sw, category: getCategoryName(sw.category) });
  };

  const excludedCategories = ['Accounting', 'Cloud Storage', 'Email/Collaboration', 'MDM', 'Virtualization'];
  const filteredCategories = categories.filter(cat => !excludedCategories.includes(cat.name));
  const displaySoftware = getDisplaySoftware();

  const renderSoftwareCard = (sw) => {
    const pricing = getPricingPreview(sw);
    return (
      <div
        key={sw.id}
        className="dash-software-card"
        onClick={() => handleSoftwareClick(sw)}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => e.key === 'Enter' && handleSoftwareClick(sw)}
      >
        <h3>{sw.name}</h3>
        {sw.pricingModel && (
          <span className="dash-pricing-model">{formatPricingModel(sw.pricingModel)}</span>
        )}
        <span className="dash-software-link">{sw.website}</span>
        <div className={`dash-pricing-preview ${pricing.type}`}>
          {pricing.price !== undefined ? (
            <span className="dash-price">${pricing.price}{pricing.label}</span>
          ) : (
            <span className="dash-price-label">{pricing.label}</span>
          )}
          {pricing.subtext && <span className="dash-price-subtext">{pricing.subtext}</span>}
        </div>
        <span className="dash-software-cta">View Details</span>
      </div>
    );
  };

  return (
    <div className="beta-dashboard">
      <header className="dash-header">
        <div className="dash-header-left">
          <button className="dash-logo" onClick={handleLogoClick}>
            Vendor<span>Scout</span>
          </button>
          <span className="dash-beta-tag">Beta</span>
        </div>
        <div className="dash-header-center">
          <form onSubmit={handleSearch} className="dash-nav-search-wrapper">
            <Search className="dash-nav-search-icon" size={16} />
            <input
              type="text"
              placeholder="Search software..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="dash-nav-search-input"
            />
          </form>
        </div>
        <div className="dash-header-right">
          {isAdmin && (
            <button className="dash-admin-btn" onClick={() => navigate('/admin')}>
              <Shield size={18} />
              Admin
            </button>
          )}
          <button className="dash-upload-btn" onClick={() => setShowUploadModal(true)}>
            <Upload size={18} />
            Upload Quote
          </button>
          <div className="dash-user-menu">
            <span className="dash-user-email">{user?.email}</span>
            <button onClick={handleLogout} className="dash-logout-btn">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      <div className="dash-layout">
        <aside className="dash-sidebar">
          <div className="dash-sidebar-header">Categories</div>
          {loading ? (
            <div className="dash-cat-skeleton">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="dash-cat-skeleton-item" />
              ))}
            </div>
          ) : (
            <ul className="dash-category-list">
              {filteredCategories.map((cat) => (
                <li key={cat.id}>
                  <button
                    className={`dash-category-btn ${selectedCategory === cat.id ? 'active' : ''}`}
                    onClick={() => setSelectedCategory(cat.id)}
                  >
                    {cat.name}
                    <ChevronRight size={16} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <main className="dash-main">
          {selectedSoftware ? (
            <SoftwareDetail software={selectedSoftware} onClose={() => setSelectedSoftware(null)} />

          ) : searchResults !== null ? (
            <div className="dash-results-section">
              <div className="dash-results-header">
                <h2>
                  {softwareLoading ? 'Searching...' : `Search results for "${searchQuery}"`}
                </h2>
                <button onClick={handleClearSearch} className="dash-clear-btn">
                  <X size={16} /> Clear
                </button>
              </div>
              {softwareLoading ? (
                <div className="dash-software-grid">
                  {[...Array(4)].map((_, i) => <SkeletonCard key={i} />)}
                </div>
              ) : searchResults.length === 0 ? (
                <p className="dash-no-results">No software found matching your search.</p>
              ) : (
                <div className="dash-software-grid">
                  {searchResults.map(sw => {
                    const pricing = getPricingPreview(sw);
                    return (
                      <div
                        key={sw.id}
                        className="dash-software-card"
                        onClick={() => handleSoftwareClick(sw)}
                        role="button"
                        tabIndex={0}
                        onKeyPress={(e) => e.key === 'Enter' && handleSoftwareClick(sw)}
                      >
                        <h3>{sw.name}</h3>
                        <div className="dash-software-meta">
                          <span className="dash-software-category">{getCategoryName(sw.category)}</span>
                          {sw.pricingModel && (
                            <span className="dash-pricing-model">{formatPricingModel(sw.pricingModel)}</span>
                          )}
                        </div>
                        <span className="dash-software-link">{sw.website}</span>
                        <div className={`dash-pricing-preview ${pricing.type}`}>
                          {pricing.price !== undefined ? (
                            <span className="dash-price">${pricing.price}{pricing.label}</span>
                          ) : (
                            <span className="dash-price-label">{pricing.label}</span>
                          )}
                          {pricing.subtext && <span className="dash-price-subtext">{pricing.subtext}</span>}
                        </div>
                        <span className="dash-software-cta">View Details</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

          ) : selectedCategory ? (
            <div className="dash-results-section">
              <div className="dash-results-header">
                <h2>{getCategoryName(selectedCategory)}</h2>
                <button onClick={() => setSelectedCategory(null)} className="dash-clear-btn">
                  <X size={16} /> Clear
                </button>
              </div>

              {/* DEV-10: Sort / Filter toolbar */}
              {!softwareLoading && software.length > 0 && (
                <div className="dash-filter-bar">
                  <div className="dash-select-wrapper">
                    <select
                      value={filterModel}
                      onChange={(e) => setFilterModel(e.target.value)}
                      className="dash-select"
                    >
                      {PRICING_MODEL_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                    <ChevronDown size={14} className="dash-select-arrow" />
                  </div>
                  <div className="dash-select-wrapper">
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="dash-select"
                    >
                      {SORT_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                    <ChevronDown size={14} className="dash-select-arrow" />
                  </div>
                  <span className="dash-result-count">{displaySoftware.length} results</span>
                </div>
              )}

              {softwareLoading ? (
                <div className="dash-software-grid">
                  {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
                </div>
              ) : displaySoftware.length === 0 ? (
                <p className="dash-no-results">
                  {filterModel ? 'No software matches the selected filter.' : 'No software in this category yet.'}
                </p>
              ) : (
                <>
                  <div className="dash-software-grid">
                    {displaySoftware.map(sw => renderSoftwareCard(sw))}
                  </div>
                  {/* DEV-11: Load More button */}
                  {hasMore && !filterModel && (
                    <div className="dash-load-more">
                      <button
                        className="dash-load-more-btn"
                        onClick={handleLoadMore}
                        disabled={loadingMore}
                      >
                        {loadingMore ? 'Loading...' : 'Load more'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

          ) : (
            <div className="dash-search-section">
              <h1>Find SaaS Pricing</h1>
              <p>Search for any software to see what others are paying.</p>
              <form onSubmit={handleSearch} className="dash-search-form">
                <div className="dash-search-wrapper">
                  <Search className="dash-search-icon" size={20} />
                  <input
                    type="text"
                    placeholder="Search for software (e.g. Salesforce, Jira, Slack...)"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="dash-search-input"
                  />
                </div>
                <button type="submit" className="dash-search-btn">Search</button>
              </form>
            </div>
          )}
        </main>
      </div>

      <QuoteUpload
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
      />
    </div>
  );
};

export default BetaDashboard;
